"use client";

import type { ReactNode } from "react";
import { usePitch } from "./PitchProvider";

/**
 * Mise en page des écrans hors application (aujourd'hui : la connexion).
 *
 * SOUS `lg`, RIEN NE CHANGE : une colonne centrée, comme depuis le premier
 * jour. C'est l'usage majoritaire et il est déjà bon.
 *
 * AU-DELÀ, l'écran se scinde en deux. La raison n'est pas décorative : une
 * carte de 384 px seule au milieu d'un moniteur de 1440 px laisse le regard
 * sans point d'appui, et le formulaire paraît flotter dans le vide. Le panneau
 * de gauche donne à cet espace un rôle — dire à quoi sert le produit — et
 * ramène le formulaire dans la moitié droite, là où l'œil arrive naturellement
 * après avoir lu.
 *
 * Le panneau est PUREMENT DÉCORATIF au sens de l'accessibilité : tout ce qui
 * est nécessaire pour agir (champs, boutons, liens) vit dans la colonne de
 * droite, qui reste seule sous `lg`. Rien ne s'y perd sur petit écran.
 */
export function AuthSplitLayout({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-1 bg-bg">
      <BrandPanel />

      {/* C'est LA PAGE qui défile, pas cette colonne. Un conteneur en
          `overflow-y-auto` combiné à un centrage vertical rend le haut du
          contenu inatteignable dès qu'il dépasse la hauteur de l'écran. Le
          panneau de gauche reste en place par `sticky`, ce qui donne le même
          effet sans le défaut. */}
      <div className="flex min-h-screen flex-1 items-center justify-center px-5 py-8 sm:px-8 sm:py-10">
        <div className="w-full max-w-sm">{children}</div>
      </div>
    </div>
  );
}

function BrandPanel() {
  // Argumentaire tiré par le layout serveur, une fois par requête.
  const { pitch, index } = usePitch();

  return (
    <aside
      // `aria-hidden` : ce panneau redit, en plus long, ce que la colonne de
      // droite annonce déjà. Le laisser lisible obligerait un lecteur d'écran à
      // traverser un argumentaire avant d'atteindre le champ email.
      aria-hidden="true"
      // `sticky` + `h-screen` : le panneau tient sa place pendant que la page
      // défile derrière lui.
      className="sticky top-0 hidden h-screen w-[46%] max-w-[620px] flex-col overflow-y-auto bg-primary-tint-2 px-12 py-14 lg:flex xl:px-16"
    >
      <div>
        <div className="flex items-center gap-3">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/icons/logo-mark.png" alt="" width={44} height={44} className="h-11 w-11" />
          <span className="text-[20px] font-bold text-primary-strong">PharmacoWork</span>
        </div>

        {/* `key` sur l'index : si l'argumentaire venait à changer, le bloc
            entier remonterait et rejouerait son entrée plutôt que d'échanger
            ses mots en place. */}
        <div key={index} className="animate-rise">
          <h2 className="mt-12 max-w-[17ch] text-[32px] font-bold leading-[1.15] text-primary-strong xl:text-[36px]">
            {pitch.headline}
          </h2>
          <p className="mt-4 max-w-[46ch] text-[15px] leading-relaxed text-primary-strong/80">
            {pitch.intro}
          </p>

          <ul className="mt-10 flex flex-col gap-5">
            {pitch.points.map(({ icon: Icon, title, detail }, i) => (
              <li
                key={title}
                className="animate-rise flex gap-3.5"
                style={{ animationDelay: `${120 + i * 70}ms` }}
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-tile bg-primary text-white">
                  <Icon size={16} strokeWidth={2} />
                </span>
                <div>
                  <p className="text-[14.5px] font-semibold leading-snug text-primary-strong">
                    {title}
                  </p>
                  <p className="mt-0.5 text-[13px] leading-snug text-primary-strong/75">
                    {detail}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </aside>
  );
}
